import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, PawPrint } from "lucide-react";
import api from "@/lib/api";
import SectionRenderer from "@/components/SectionRenderer";
import FaqSection from "@/components/FaqSection";
import Disclaimer from "@/components/Disclaimer";
import Seo from "@/components/Seo";
import RelatedGuides from "@/components/RelatedGuides";
import ChecklistSubscribe from "@/components/ChecklistSubscribe";
import ShareGuide from "@/components/ShareGuide";
import { PetStoriesStrip } from "@/components/PetStoriesStrip";
import { WhereWeDrive } from "@/components/WhereWeDrive";
import { useLang } from "@/context/LanguageContext";

const CHECKLIST_SLUGS = ["residency-documents", "fmm-land-entry", "driving", "pets", "arrival-settling"];

const SEO_OVERRIDES = {
  "your-route": {
    title: "Your Move to Mexico: The Complete Relocation Timeline",
    description: "The full relocation timeline from first decision to your first settled morning in Mexico. Five steps, no surprises.",
  },
  "start-here": {
    description: "Not sure where to begin your move to Mexico? Take the Start Here quiz and get a personalized roadmap in two minutes.",
  },
  "pets": {
    description: "Moving to Mexico with pets: vet paperwork, travel crates, border rules and door-to-door pet transport, handled calmly.",
  },
  "pet-transport": {
    description: "We drive your pets from the US to Mérida door-to-door — leash walks, pet-friendly hotels and photo updates at every stop.",
  },
  "drivers-transport": {
    description: "Drivers and transport support for your move to Mexico — background-checked bilingual drivers covering 8 cities and border crossings.",
  },
};

export default function ContentPage({ slug }) {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const { lang } = useLang();
  const tr = (en, es) => (lang === "es" && es ? es : en);

  const load = () => {
    setError(false);
    setPage(null);
    api
      .get(`/content/page/${slug}`, { timeout: 15000 })
      .then((res) => setPage(res.data))
      .catch(() => setError(true));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid={`page-${slug}-error`}>
        <h1 className="text-2xl font-normal text-charcoal-800">We couldn't load this page</h1>
        <p className="mt-3 text-charcoal-600">
          Something went wrong reaching our content. Please check your connection and try again.
        </p>
        <button
          onClick={load}
          data-testid={`page-${slug}-retry`}
          className="mt-6 rounded-full bg-terracotta px-6 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!page) {
    return <div className="mx-auto max-w-7xl px-6 py-32 text-charcoal-500">Loading…</div>;
  }

  return (
    <div data-testid={`page-${slug}`}>
      <Seo
        title={SEO_OVERRIDES[slug]?.title || page.hero_title || page.nav_label}
        description={SEO_OVERRIDES[slug]?.description || page.hero_subtitle || page.intro || ""}
        image={page.feature_image || page.hero_image}
        path={`/${slug}`}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={page.hero_image} alt="" className="h-full w-full object-cover" style={{ objectPosition: page.hero_position || "center" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-sand-100 via-sand-100/85 to-sand-100/30" />
        </div>
        <div className="absolute inset-0 pattern-talavera opacity-[0.05]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-2xl animate-fade-up">
            <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-4">
              {page.nav_label}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-charcoal-900 leading-[1.05]">
              {tr(page.hero_title, page.hero_title_es)}
            </h1>
            <p className="mt-6 text-lg text-charcoal-600 leading-relaxed">{tr(page.hero_subtitle, page.hero_subtitle_es)}</p>
            {CHECKLIST_SLUGS.includes(slug) && (
              <a
                data-testid={`page-${slug}-checklist-download`}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/checklists/${slug}/pdf`}
                onClick={() => setDownloaded(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-charcoal-800 px-6 py-3 text-sm text-white font-medium hover:bg-charcoal-900 transition-colors"
              >
                <FileDown size={16} /> Download the free checklist (PDF)
              </a>
            )}
            <div className="mt-6">
              <ShareGuide slug={slug} title={tr(page.hero_title, page.hero_title_es) || page.nav_label} />
            </div>
            {slug === "pet-transport" && (
              <button
                data-testid="pet-quote-cta"
                onClick={() => window.dispatchEvent(new CustomEvent("hola:open-chat", { detail: { petRide: true } }))}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3 text-sm text-white font-medium hover:bg-terracotta-hover transition-colors"
              >
                <PawPrint size={16} /> Get my pet quote
              </button>
            )}
            {downloaded && <ChecklistSubscribe slug={slug} />}
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24 space-y-16 lg:space-y-24">
        {(page.summary || page.last_updated) && (
          <div className="max-w-3xl" data-testid={`page-${slug}-summary`}>
            {page.last_updated && (
              <p className="text-xs uppercase tracking-wider text-charcoal-400 mb-4">
                Last updated: {page.last_updated}
              </p>
            )}
            {page.summary && (
              <p className="text-lg leading-relaxed text-charcoal-700 bg-sage-light border-l-4 border-sage rounded-r-2xl px-6 py-5">
                {tr(page.summary, page.summary_es)}
              </p>
            )}
          </div>
        )}
        {page.intro && (
          <p className="max-w-3xl text-xl leading-relaxed text-charcoal-700 font-serif font-light">
            {tr(page.intro, page.intro_es)}
          </p>
        )}
        {page.feature_image && (
          <figure className="overflow-hidden rounded-3xl border border-sand-300 shadow-[0_8px_32px_rgba(42,38,36,0.06)]">
            <img
              src={page.feature_image}
              alt={page.feature_caption || page.nav_label}
              className="h-[300px] lg:h-[440px] w-full object-cover"
            />
            {page.feature_caption && (
              <figcaption className="bg-white px-6 py-4 text-sm text-charcoal-500">
                {page.feature_caption}
              </figcaption>
            )}
          </figure>
        )}
        {(page.sections || []).map((s, i) => (
          <SectionRenderer key={i} section={s} />
        ))}
        {slug === "drivers-transport" && <WhereWeDrive />}
        {["pets", "pet-transport"].includes(slug) && <PetStoriesStrip />}
        {page.what_can_change && (
          <div className="max-w-3xl rounded-2xl border border-terracotta/40 bg-terracotta-light/40 px-6 py-5" data-testid={`page-${slug}-what-changes`}>
            <h3 className="font-medium text-charcoal-800 mb-1">Good to know — things that can change</h3>
            <p className="text-charcoal-600 leading-relaxed">{page.what_can_change}</p>
          </div>
        )}
        {page.disclaimer && (
          <div className="max-w-3xl">
            <Disclaimer text={page.disclaimer} testid={`page-${slug}-disclaimer`} />
          </div>
        )}
      </div>

      <FaqSection page={slug} />

      <RelatedGuides slug={slug} />

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pb-8">
        <div className="rounded-3xl bg-sage-light p-10 lg:p-16 text-center">
          {slug !== "merida" && (
            <p className="mb-4 text-sm text-charcoal-600">
              We're <Link to="/merida" className="font-medium text-terracotta underline underline-offset-2">based in Mérida</Link> — our primary destination. Wondering if Mérida or another city fits your move better?
            </p>
          )}
          <h2 className="text-3xl font-normal text-charcoal-800">We're with you every step of the way</h2>
          <p className="mt-3 text-charcoal-600 max-w-xl mx-auto">
            You don't have to figure this out alone. Book a 45-minute intro call, or send us a message — whatever feels right for you.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/book" data-testid="content-book-cta" className="rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              Book an intro call
            </Link>
            <Link to="/contact" data-testid="content-contact-cta" className="rounded-full border border-charcoal-300 px-8 py-3 text-charcoal-800 font-medium hover:bg-sand-200 transition-colors">
              Send a message
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
